import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { colorTokens } from '../../tokens/globals/tokens';

export interface OutlinerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Element that should be outlined */
  children: ReactNode;
  /** Semantic variant, based on intent */
  variant?: 'ink' | 'custom' | 'staff';
  /** Specifies what is written in the tag */
  label: string;
  /** Specifies the display behavior */
  display?: React.CSSProperties['display'];
  /** Puts the tag outside the border */
  tagOut?: boolean;
  /** Hides the outline and the tag */
  hidden?: boolean;
  /** Provides a way to hide content from end users */
  hideContent?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const variantColors = {
  ink: 'rgb(58 129 194 / 75%)',
  staff: 'rgb(236 145 116 / 75%)',
  custom: 'rgb(191 38 128 / 75%)',
};

const StyledOutliner = styled.div`
  position: relative;
  outline: ${({ theme }) => (theme.hidden ? 'none' : `2px solid ${variantColors[theme.variant]}`)};
  ${({ theme }) => theme.display && `display: ${theme.display};`};

  &::after {
    content: ${({ theme }) => (theme.hidden ? '' : 'attr(data-tag);')}
    position: absolute;
    top: ${({ theme }) => (theme.tagOut ? '-17px' : 0)};
    right: ${({ theme }) => (theme.tagOut ? '-2px' : 0)};
    padding: ${({ theme }) => (theme.tagOut ? '3px 6px 1px' : '3px 4px 3px 6px')};
    background-color: ${({ theme }) => variantColors[theme.variant]};
    color: ${({ theme }) => (theme.variant === 'staff' ? colorTokens.GlobalBrandBlack : colorTokens.GlobalBrandWhite)};
    font-size: 11px;
    font-weight: 500;
    line-height: 11px;
    user-select: none;
    pointer-events: none;
    z-index: 1;
  }
`;

/**
 * @purpose Highlights parts of the UI
 * @status beta
 */
function Outliner(props: OutlinerProps) {
  const { style, className, display, tagOut, variant, label, hidden, hideContent, ...otherProps } = props;

  return hideContent ? null : (
    <StyledOutliner
      theme={{ variant, tagOut, display, hidden }}
      data-testid={props['data-testid']}
      data-tag={label}
      {...otherProps}
    />
  );
}

Outliner.defaultProps = {
  children: null,
  variant: 'custom',
  label: 'Label',
  display: 'block',
  tagOut: false,
  hidden: false,
  hideContent: false,
};

Outliner.displayName = 'Outliner';

export default Outliner;
